import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, Send, Save, FileText, Eye, CreditCard } from 'lucide-react';
import Spinner from './Spinner';
import InvoicePreview from './InvoicePreview';
import { billingApi, CreateInvoiceRequest, BackendInvoiceStatus, InvoicePaymentOption } from '../api/billingApi';
import { getBranding, BrandingData } from '../api/brandingApi';
import { toast } from 'react-toastify';

interface Item {
  id: string;
  description: string;
  quantity: number;
  price: number;
}

interface InvoiceFormProps {
  onInvoiceCreated: (newInvoiceId: string) => void;
  initialData?: any | null;
}

export default function InvoiceForm({ onInvoiceCreated, initialData }: InvoiceFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isMarkPaidModalOpen, setIsMarkPaidModalOpen] = useState(false);
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [paymentTermsDays, setPaymentTermsDays] = useState(7);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentOption, setPaymentOption] = useState<InvoicePaymentOption>(
    InvoicePaymentOption.None
  );

  const [externalPaymentUrl, setExternalPaymentUrl] = useState('');
  const [externalPaymentLabel, setExternalPaymentLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<Item[]>([
    { id: '1', description: 'Website Maintenance - Monthly', quantity: 1, price: 150 },
    { id: '2', description: 'SEO Optimization - Quarterly', quantity: 1, price: 300 },
  ]);

  useEffect(() => {
    if (initialData) {
      setClientName(initialData.customerName || '');
      setClientEmail(initialData.customerEmail || '');
      setCustomerAddress(initialData.customerAddress || '');
      setCustomerPhone(initialData.customerPhone || '');
      setPaymentTermsDays(initialData.paymentTermsDays || 7);
      setIssueDate(initialData.issueDate ? initialData.issueDate.split('T')[0] : new Date().toISOString().split('T')[0]);
      setNotes(initialData.notes || '');

      setPaymentOption(
        initialData.paymentOption ?? InvoicePaymentOption.None
      );

      setExternalPaymentUrl(
        initialData.paymentOption === InvoicePaymentOption.ExternalLink
          ? initialData.paymentLink ?? ''
          : ''
      );

      setExternalPaymentLabel(
        initialData.paymentOption === InvoicePaymentOption.ExternalLink
          ? initialData.paymentLabel ?? ''
          : ''
      );

      if (initialData.items && initialData.items.length > 0) {
        setItems(initialData.items.map((it: any, idx: number) => ({
          id: it.id || idx.toString(),
          description: it.description,
          quantity: it.quantity,
          price: it.unitPrice
        })));
      }
    } else {
      resetForm();
    }
  }, [initialData]);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const data = await getBranding();
        if (data) setBranding(data);
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    fetchBranding();
  }, []);

  const addItem = () => {
    setItems([...items, { id: Date.now().toString(), description: '', quantity: 1, price: 0 }]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) setItems(items.filter(item => item.id !== id));
  };

  const updateItem = (id: string, field: keyof Item, value: string | number) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const computedDueDate = useMemo(() => {
    if (!issueDate) return '';
    const date = new Date(issueDate);
    date.setDate(date.getDate() + (Number(paymentTermsDays) || 7));
    return date.toISOString().split('T')[0];
  }, [issueDate, paymentTermsDays]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const tax = subtotal * (branding?.taxAmount || 0) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [items, branding]);

  const hasValidExternalPaymentLink =
    paymentOption !== InvoicePaymentOption.ExternalLink ||
    externalPaymentUrl.trim() !== '';

  const hasValidStripePayment =
    paymentOption !== InvoicePaymentOption.Stripe ||
    branding?.stripeConnected === true;

  const isFormValid =
    clientName.trim() !== '' &&
    items.length > 0 &&
    hasValidExternalPaymentLink &&
    hasValidStripePayment;

  const validatePaymentOption = () => {
    if (
      paymentOption === InvoicePaymentOption.Stripe &&
      !branding?.stripeConnected
    ) {
      toast.error('Connect Stripe before using Stripe payments.');
      return false;
    }

    if (paymentOption === InvoicePaymentOption.ExternalLink) {
      if (!externalPaymentUrl.trim()) {
        toast.error('External payment URL is required.');
        return false;
      }

      try {
        const url = new URL(externalPaymentUrl.trim());

        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
          throw new Error();
        }
      } catch {
        toast.error('Enter a valid payment URL.');
        return false;
      }
    }

    return true;
  };

  const resetForm = () => {
    setClientName('');
    setClientEmail('');
    setCustomerAddress('');
    setCustomerPhone('');
    setPaymentTermsDays(7);
    setIssueDate(new Date().toISOString().split('T')[0]);
    setNotes('');

    setPaymentOption(InvoicePaymentOption.None);
    setExternalPaymentUrl('');
    setExternalPaymentLabel('');

    setItems([{ id: '1', description: 'Website Maintenance - Monthly', quantity: 1, price: 150 }]);
  };

  const handleSendInvoice = async () => {
    if (!validatePaymentOption()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const invoiceData: CreateInvoiceRequest = {
        customerName: clientName,
        customerEmail: clientEmail,
        customerAddress,
        customerPhone,
        paymentTermsDays: Number(paymentTermsDays),
        issueDate: new Date(issueDate).toISOString(),
        notes: notes.trim() || null,
        items: items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.price,
        })),
        paymentOption,

        externalPaymentUrl:
          paymentOption === InvoicePaymentOption.ExternalLink
            ? externalPaymentUrl.trim()
            : null,

        externalPaymentLabel:
          paymentOption === InvoicePaymentOption.ExternalLink
            ? externalPaymentLabel.trim() || null
            : null,
        sendEmail: true,
      };
      
      if (initialData?.id) {
        // Update existing invoice and send email
        const updatedInvoice = await billingApi.updateInvoice(initialData.id, invoiceData);
        toast.success(`Invoice ${updatedInvoice.invoiceNumber} updated and sent successfully!`);
        onInvoiceCreated(updatedInvoice.id);
      } else {
        // Create new invoice and send email
        const newInvoice = await billingApi.createInvoice(invoiceData);
        toast.success(`Invoice ${newInvoice.invoiceNumber} created successfully!`);
        onInvoiceCreated(newInvoice.id);
        resetForm(); // Call the resetForm function to clear all fields
      }
    } catch (error) {
      // Error handled by billingApi, just log here if needed
      console.error("Failed to send invoice:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    setIsSubmitting(true);
    try {
      const invoiceData: CreateInvoiceRequest = {
        customerName: clientName,
        customerEmail: clientEmail,
        customerAddress,
        customerPhone,
        paymentTermsDays: Number(paymentTermsDays),
        issueDate: new Date(issueDate).toISOString(),
        notes: notes.trim() || null,
        items: items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.price,
        })),
        paymentOption,

        externalPaymentUrl:
          paymentOption === InvoicePaymentOption.ExternalLink
            ? externalPaymentUrl.trim()
            : null,

        externalPaymentLabel:
          paymentOption === InvoicePaymentOption.ExternalLink
            ? externalPaymentLabel.trim() || null
            : null,
        sendEmail: false,
      };

      if (initialData?.id) {
        await billingApi.updateInvoice(initialData.id, invoiceData);
        toast.success("Draft updated successfully");
      } else {
        const res = await billingApi.createInvoice(invoiceData);
        toast.success(`Draft ${res.invoiceNumber} saved`);
      }
      onInvoiceCreated(initialData?.id || 'new');
    } catch (err) {
      toast.error("Failed to save draft");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!initialData?.id) return toast.info("Please save the invoice first to download PDF");
    try {
      const blob = await billingApi.downloadPdf(initialData.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${initialData.invoiceNumber}.pdf`;
      a.click();
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handlePreview = async () => {
    if (!branding) {
      const data = await getBranding();
      setBranding(data);
    }
    setIsPreviewOpen(true);
  };

  const handlePayment = async () => {
    if (!initialData?.id) return;
    const amount = parseFloat(markPaidAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    const remainingBalance = initialData?.remainingBalance || (totals.total - (initialData?.paidAmount || 0));
    if (amount > remainingBalance) {
      toast.error(`Payment cannot exceed remaining balance of $${remainingBalance.toFixed(2)}`);
      return;
    }
    
    setIsSubmitting(true);
    try {
      await billingApi.markAsPaid(initialData.id, amount);
      toast.success(`Invoice marked as paid with amount $${amount.toFixed(2)}`);
      setMarkPaidAmount('');
      setIsMarkPaidModalOpen(false);
      onInvoiceCreated(initialData.id); // Refresh invoice data
    } catch (error) {
      console.error("Failed to mark invoice as paid:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="flex flex-col xl:grid xl:grid-cols-3 gap-8">
      <div className="xl:col-span-2 space-y-6">
        {/* Customer Section */}
        <div className="card p-5 sm:p-8 bg-white rounded-3xl border border-strivre-lightnavy shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-6">Customer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Client Name</label>
              <input 
                type="text" 
                placeholder="e.g. Acme Corp" 
                className="input-base" 
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </div>
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Client Email</label>
              <input 
                type="email" 
                placeholder="billing@acme.com" 
                className="input-base" 
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-6 space-y-2 text-left">
            <label className="text-sm font-semibold text-slate-700">Customer Address</label>
            <textarea 
              placeholder="Street, City, Country" 
              className="input-base min-h-[80px]" 
              value={customerAddress}
              onChange={(e) => setCustomerAddress(e.target.value)}
            />
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Phone / WhatsApp</label>
              <input 
                type="text" 
                placeholder="+1 234 567 890" 
                className="input-base" 
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="card p-5 sm:p-8 bg-white rounded-3xl border border-strivre-lightnavy shadow-sm">
          <div className="flex justify-between items-center mb-6 text-left">
            <h3 className="text-lg font-bold text-slate-900">Line Items</h3>
            <button onClick={addItem} className="text-strivre-burgundy hover:text-red-900 font-bold text-sm flex items-center gap-1">
              <Plus size={18} /> Add Item
            </button>
          </div>
          
          <div className="space-y-4">
            {items.map((item, idx) => (
              <div key={item.id} className="flex flex-col md:flex-row gap-4 items-start md:items-end p-4 md:p-0 bg-slate-50/50 md:bg-transparent rounded-2xl md:rounded-none animate-fade-in" style={{ animationDelay: `${idx * 50}ms` }}>
                <div className="flex-1 w-full space-y-2 text-left">
                  <label className={`text-[10px] font-black text-slate-400 uppercase tracking-widest ${idx > 0 ? 'md:hidden' : ''}`}>Description</label>
                  <input 
                    type="text" 
                    value={item.description} 
                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                    className="input-base h-11" 
                    placeholder="Item name..."
                  />
                </div>
                <div className="w-full md:w-24 space-y-2 text-left">
                  <label className={`text-[10px] font-black text-slate-400 uppercase tracking-widest ${idx > 0 ? 'md:hidden' : ''}`}>Qty</label>
                  <input 
                    type="number" 
                    value={item.quantity} 
                    onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                    className="input-base h-11" 
                  />
                </div>
                <div className="w-full md:w-32 space-y-2 text-left">
                  <label className={`text-[10px] font-black text-slate-400 uppercase tracking-widest ${idx > 0 ? 'md:hidden' : ''}`}>Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input 
                      type="number" 
                      value={item.price} 
                      onChange={(e) => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                      className="input-base h-11 pl-7" 
                    />
                  </div>
                </div>
                <div className="flex items-center h-11 px-2">
                  <button 
                    onClick={() => removeItem(item.id)}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Method */}
        <div className="card p-5 sm:p-8 bg-white rounded-3xl border border-strivre-lightnavy shadow-sm">
          <div className="mb-6 text-left">
            <h3 className="text-lg font-bold text-slate-900">
              Payment Method
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Choose how your customer can pay this invoice.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* No Online Payment */}
            <button
              type="button"
              onClick={() => setPaymentOption(InvoicePaymentOption.None)}
              className={`rounded-2xl border p-5 text-left transition-all ${
                paymentOption === InvoicePaymentOption.None
                  ? 'border-strivre-burgundy bg-strivre-lightburgundy'
                  : 'border-strivre-lightnavy bg-white hover:border-strivre-burgundy'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    paymentOption === InvoicePaymentOption.None
                      ? 'bg-strivre-burgundy text-white'
                      : 'bg-strivre-softgrey text-slate-500'
                  }`}
                >
                  <FileText size={18} />
                </div>

                <div>
                  <p className="font-bold text-slate-900">
                    No Online Payment
                  </p>

                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    Send the invoice without a payment link.
                  </p>
                </div>
              </div>
            </button>

            {/* Stripe */}
            <button
              type="button"
              disabled={!branding?.stripeConnected}
              onClick={() => setPaymentOption(InvoicePaymentOption.Stripe)}
              className={`rounded-2xl border p-5 text-left transition-all ${
                !branding?.stripeConnected
                  ? 'cursor-not-allowed border-slate-100 bg-slate-50 opacity-60'
                  : paymentOption === InvoicePaymentOption.Stripe
                    ? 'border-strivre-burgundy bg-strivre-lightburgundy'
                    : 'border-strivre-lightnavy bg-white hover:border-strivre-burgundy'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    paymentOption === InvoicePaymentOption.Stripe
                      ? 'bg-strivre-burgundy text-white'
                      : 'bg-strivre-softgrey text-slate-500'
                  }`}
                >
                  <CreditCard size={18} />
                </div>

                <div>
                  <p className="font-bold text-slate-900">
                    Stripe
                  </p>

                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    {branding?.stripeConnected
                      ? 'Accept payment through your connected Stripe account.'
                      : 'Connect Stripe to enable this option.'}
                  </p>
                </div>
              </div>
            </button>

            {/* External Payment */}
            <button
              type="button"
              onClick={() => setPaymentOption(InvoicePaymentOption.ExternalLink)}
              className={`rounded-2xl border p-5 text-left transition-all ${
                paymentOption === InvoicePaymentOption.ExternalLink
                  ? 'border-strivre-burgundy bg-strivre-lightburgundy'
                  : 'border-strivre-lightnavy bg-white hover:border-strivre-burgundy'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    paymentOption === InvoicePaymentOption.ExternalLink
                      ? 'bg-strivre-burgundy text-white'
                      : 'bg-strivre-softgrey text-slate-500'
                  }`}
                >
                  <CreditCard size={18} />
                </div>

                <div>
                  <p className="font-bold text-slate-900">
                    External Payment Link
                  </p>

                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    Use GoCardless or another payment service.
                  </p>
                </div>
              </div>
            </button>
          </div>

          {paymentOption === InvoicePaymentOption.ExternalLink && (
            <div className="mt-6 grid grid-cols-1 gap-5 border-t border-slate-100 pt-6 md:grid-cols-2">
              <div className="space-y-2 text-left">
                <label className="text-sm font-semibold text-slate-700">
                  Payment Button Label
                </label>

                <input
                  type="text"
                  className="input-base w-full"
                  placeholder="e.g. Pay with GoCardless"
                  value={externalPaymentLabel}
                  onChange={(event) => setExternalPaymentLabel(event.target.value)}
                />

                <p className="text-xs text-slate-400">
                  Customers will see this text on the payment button.
                </p>
              </div>

              <div className="space-y-2 text-left">
                <label className="text-sm font-semibold text-slate-700">
                  Payment URL
                </label>

                <input
                  type="url"
                  className="input-base w-full"
                  placeholder="https://pay.example.com/..."
                  value={externalPaymentUrl}
                  onChange={(event) => setExternalPaymentUrl(event.target.value)}
                  required
                />

                <p className="text-xs text-slate-400">
                  Enter the secure payment link provided by your payment service.
                </p>
              </div>
            </div>
          )}

          {paymentOption === InvoicePaymentOption.None && (
            <div className="mt-5 rounded-xl border border-strivre-lightnavy bg-strivre-softgrey px-4 py-3 text-left">
              <p className="text-sm text-slate-600">
                This invoice will be sent without an online payment button.
                You can record payments manually using Apply Payment.
              </p>
            </div>
          )}

          {paymentOption === InvoicePaymentOption.Stripe &&
            branding?.stripeConnected && (
              <div className="mt-5 rounded-xl border border-strivre-lightnavy bg-strivre-softgrey px-4 py-3 text-left">
                <p className="text-sm text-slate-600">
                  A Stripe Checkout link will be generated when the invoice is sent.
                </p>
              </div>
            )}
        </div>

        {/* Settings */}
        <div className="card p-5 sm:p-8 bg-white rounded-3xl border border-strivre-lightnavy shadow-sm">
           <h3 className="text-lg font-bold text-slate-900 mb-6">Additional Details</h3>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Issue Date</label>
              <input 
                type="date" 
                className="input-base" 
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
              />
            </div>
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Payment Terms</label>
              <select 
                className="input-base" 
                value={paymentTermsDays}
                onChange={(e) => setPaymentTermsDays(Number(e.target.value))}
              >
                <option value={7}>7 Days</option>
                <option value={14}>14 Days</option>
                <option value={30}>30 Days</option>
              </select>
            </div>
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Due Date (Auto)</label>
              <input 
                type="text" 
                readOnly 
                className="input-base bg-slate-50 text-slate-500 cursor-not-allowed" 
                value={new Date(computedDueDate).toLocaleDateString()}
              />
            </div>
            <div className="space-y-2 text-left">
              <label className="text-sm font-semibold text-slate-700">Notes</label>
              <textarea 
                placeholder="Terms and conditions..." 
                className="input-base min-h-[100px]" 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Sidebar */}
      <div className="space-y-6">
        <div className="card p-6 sm:p-8 bg-strivre-navy text-white rounded-3xl border border-strivre-lightnavy shadow-xl static xl:sticky xl:top-8">
          <h3 className="text-xl font-bold mb-8">Summary</h3>
          
          <div className="space-y-4 mb-8">
            <div className="flex justify-between text-slate-400 font-medium">
              <span>Subtotal</span>
              <span>${totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-400 font-medium">
              <span>Tax ({branding?.taxAmount || 0}%)</span>
              <span>${totals.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-400 font-medium">
              <span>Payment</span>

              <span className="text-right">
                {paymentOption === InvoicePaymentOption.Stripe
                  ? 'Stripe'
                  : paymentOption === InvoicePaymentOption.ExternalLink
                    ? externalPaymentLabel.trim() || 'External Link'
                    : 'No Online Payment'}
              </span>
            </div>
            <div className="flex justify-between text-slate-400 font-medium italic">
              <span>Due Date</span>
              <span>{new Date(computedDueDate).toLocaleDateString()}</span>
            </div>
            <div className="pt-4 border-t border-strivre-lightnavy flex justify-between items-end">
              <span className="text-slate-400 font-bold uppercase text-xs tracking-[0.2em]">Total</span>
              <span className="text-4xl font-black tracking-tighter text-white">${totals.total.toFixed(2)}</span>
            </div>
          </div>

          {initialData?.paidAmount !== undefined && initialData.paidAmount > 0 && (
            <div className="space-y-4 mb-8 pt-4 border-t border-strivre-lightnavy">
              <div className="flex justify-between text-emerald-400 font-medium text-sm">
                <span>Paid Amount</span>
                <span>${initialData.paidAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-red-400 font-bold">
                <span>Balance Due</span>
                <span>${(totals.total - initialData.paidAmount).toLocaleString()}</span>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {(initialData?.status === BackendInvoiceStatus.Sent || initialData?.status === BackendInvoiceStatus.PartialPaid) && (
              <button 
                disabled={isSubmitting}
                onClick={() => setIsMarkPaidModalOpen(true)}
                className="w-full py-4 rounded-2xl flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-all shadow-lg mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCard size={18} /> Apply Payment
              </button>
            )}
            <button 
              disabled={isSubmitting || !isFormValid}
              onClick={handleSendInvoice}
              className="btn-primary w-full py-4 rounded-2xl flex items-center justify-center gap-2"
            >
              {isSubmitting ? <Spinner size="h-5 w-5" /> : <><Send size={18} /> {initialData ? 'Update & Send' : 'Send Invoice'}</>}
            </button>
            
            <button 
                onClick={() => handlePreview()}
                className="btn-secondary w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 border-strivre-lightnavy text-white bg-white/10 hover:bg-slate-700 disabled:opacity-50"
              >
                <Eye size={18} /> Preview Invoice
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button 
                disabled={isSubmitting}
                onClick={handleSaveDraft}
                className="p-3 rounded-2xl bg-slate-900 text-slate-400 hover:text-white transition-all flex items-center justify-center gap-2 text-sm font-bold border border-strivre-lightnavy"
              >
                <Save size={16} /> {initialData ? 'Save Changes' : 'Draft'}
              </button>
              <button 
                onClick={handleGeneratePDF}
                className="p-3 rounded-2xl bg-slate-900 text-slate-400 hover:text-white transition-all flex items-center justify-center gap-2 text-sm font-bold border border-strivre-lightnavy"
              >
                <FileText size={16} /> PDF
              </button>
            </div>
          </div>
          
          <p className="mt-6 text-[11px] text-slate-500 text-center font-medium uppercase tracking-widest">
            Powered by Strivre Billing
          </p>
        </div>
      </div>
    </div>

    <InvoicePreview 
      isOpen={isPreviewOpen} 
      onClose={() => setIsPreviewOpen(false)} 
      branding={branding}
      data={{
        clientName,
        clientEmail,
        customerAddress,
        customerPhone,
        dueDate: computedDueDate,
        notes,
        items,
        totals
      }}
    />

    {/* Mark As Paid Modal */}
    {isMarkPaidModalOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm w-full mx-4 shadow-xl">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Apply Payment</h2>
          <p className="text-sm text-slate-600 mb-6">Enter the amount paid for this invoice.</p>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Amount to Pay</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                <input 
                  type="number" 
                  placeholder="0.00"
                  value={markPaidAmount}
                  onChange={(e) => setMarkPaidAmount(e.target.value)}
                  className="input-base pl-7 w-full" 
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Invoice Total:</span>
                <span className="font-bold text-slate-900">${totals.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Already Paid:</span>
                <span className="font-bold text-emerald-600">${(initialData?.paidAmount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-strivre-lightnavy">
                <span className="text-slate-600">Remaining Balance:</span>
                <span className="font-bold text-slate-900">${(initialData?.remainingBalance || (totals.total - (initialData?.paidAmount || 0))).toFixed(2)}</span>
              </div>
            </div>

            {markPaidAmount && parseFloat(markPaidAmount) > (initialData?.remainingBalance || (totals.total - (initialData?.paidAmount || 0))) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700 font-medium">⚠️ Payment amount exceeds remaining balance</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button 
                onClick={() => {
                  setIsMarkPaidModalOpen(false);
                  setMarkPaidAmount('');
                }}
                className="flex-1 py-2 px-4 rounded-lg border border-strivre-lightnavy text-slate-700 font-medium hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button 
                disabled={isSubmitting || !markPaidAmount || parseFloat(markPaidAmount) <= 0 || parseFloat(markPaidAmount) > (initialData?.remainingBalance || (totals.total - (initialData?.paidAmount || 0)))}
                onClick={handlePayment}
                className="flex-1 py-2 px-4 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Spinner size="h-4 w-4" /> : 'Apply Payment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}